import { useState } from "react";
import {
  IconBolt,
  IconEdit,
  IconCalendar,
  IconLink,
  IconTrash,
  IconPlus,
  IconVideo,
  IconDeviceLaptop,
} from "@tabler/icons-react";
import { useQuickLinks } from "../../../../utils/hooks/useQuickLinks";
import {
  Button,
  Loader,
  Modal,
  Select,
  TextInput,
  Grid,
  ActionIcon,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { openNewTab } from "@/utils/browserUtils";
import "./TaxProLinks.scss";

// Define the type for a link
type Link = {
  id?: number;
  linkType: string;
  name: string;
  url: string;
};

const MAX_LINKS = 5;
// Set the default linkType to "Other" if it is empty
const DEFAULT_LINK: Link = { linkType: "Other", name: "", url: "" };

const TaxProLinks = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [links, setLinks] = useState<Link[]>([DEFAULT_LINK]); // Use the Link type for the state
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [originalLinks, setOriginalLinks] = useState<Link[]>([]);

  const {
    quickLinks,
    isLoadingQuickLinks,
    isErrorQuickLinks,
    modifyQuickLink,
    quickLinksError,
    addQuickLink,
    removeQuickLink,
  } = useQuickLinks();

  // When opening modal, load existing links into the form
  const handleEditLinks = () => {
    if (quickLinks && quickLinks.length > 0) {
      const mappedLinks = quickLinks.map((link) => ({
        id: link.id,
        linkType: link.linkType || "Other",
        name: link.name || "",
        url: link.url || "",
      }));
      setLinks(mappedLinks);
      setOriginalLinks(mappedLinks); // Store the original state of the links
    } else {
      setLinks([DEFAULT_LINK]);
      setOriginalLinks([DEFAULT_LINK]);
    }
    open();
  };

  // Add a new blank link form, if less than MAX_LINKS
  const addLinkForm = () => {
    if (links.length < MAX_LINKS) {
      setLinks((prevLinks) => [...prevLinks, { ...DEFAULT_LINK }]);
    }
  };

  // Render the list of quick links
  const renderQuickLinks = () => {
    if (quickLinks && quickLinks.length > 0) {
      return (
        <ul className="quick-links-list">
          {quickLinks.map((link) => (
            <li
              key={link.id}
              onClick={() => openNewTab(link.url)}
              className="clickable-link"
            >
              <a href={link.url} target="_blank" rel="noopener noreferrer">
                {getLinkIcon(link.linkType)}
                <span className="link-type">{link.name}</span>
              </a>
            </li>
          ))}
        </ul>
      );
    } else {
      return <p className="no-links-message">No quick links available.</p>;
    }
  };

  // Update the values of individual link forms
  const updateLink = (index: number, field: string, value: string | null) => {
    setLinks((prevLinks) =>
      prevLinks.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      )
    );
  };

  // Delete a link from the list and send a delete request to the backend
  const deleteLink = (index: number, linkId?: number) => {
    if (linkId) {
      removeQuickLink(linkId); // Trigger the backend delete request
    }
    setLinks((prevLinks) => prevLinks.filter((_, i) => i !== index));
  };

  // Save all valid links and reset the form
  // Helper to check if a link has been modified
  const isLinkModified = (originalLink: Link, updatedLink: Link): boolean => {
    return (
      originalLink.url !== updatedLink.url ||
      originalLink.linkType !== updatedLink.linkType ||
      originalLink.name !== updatedLink.name
    );
  };

  // Helper to update an existing link if it's modified
  const updateLinkIfModified = (originalLink: Link, updatedLink: Link) => {
    if (isLinkModified(originalLink, updatedLink)) {
      modifyQuickLink({
        id: updatedLink.id!,
        linkType: updatedLink.linkType,
        name: updatedLink.name,
        url: updatedLink.url,
      });
    }
  };

  // Helper to add a new link
  const addNewLink = (link: Link) => {
    if (link.linkType && link.name && link.url) {
      addQuickLink({
        linkType: link.linkType,
        name: link.name,
        url: link.url,
      });
    }
  };

  // Main save function
  const saveLinks = () => {
    links.forEach((link, index) => {
      if (link.id) {
        // Existing link: check for modifications and update if necessary
        const originalLink = originalLinks[index];
        if (originalLink) {
          updateLinkIfModified(originalLink, link);
        }
      } else {
        // New link: add it
        addNewLink(link);
      }
    });

    resetForm(); // Reset form after saving
  };

  // Reset form to initial state
  const resetForm = () => {
    setLinks([DEFAULT_LINK]);
    setIsAddingLink(false);
    close();
  };

  // Get icon based on the link type
  const getLinkIcon = (type: string) => {
    switch (type) {
      case "Software":
        return <IconDeviceLaptop className="link-icon" size={34} />;
      case "Calendar":
        return <IconCalendar className="link-icon" size={34} />;
      case "Video Conferencing":
        return <IconVideo className="link-icon" size={34} />;
      default:
        return <IconLink className="link-icon" size={34} />;
    }
  };

  // Check if Save button should be disabled
  const isSaveDisabled = links.some(
    ({ linkType, name, url }) => !linkType || !name || !url
  );

  // Render each link form with a delete icon
  const renderLinkForms = () =>
    links.map((link, index) => {
      return (
        <Grid key={link.id ?? index} className="link-row" gutter="sm">
          <Grid.Col span={3}>
            <Select
              label="Link Type"
              placeholder="Select type"
              data={[
                { value: "Software", label: "Software" },
                { value: "Calendar", label: "Calendar" },
                {
                  value: "Video Conferencing",
                  label: "Video Conferencing",
                },
                { value: "Other", label: "Other" },
              ]}
              value={link.linkType}
              onChange={(value) => updateLink(index, "linkType", value)}
              required
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput
              label="Name"
              placeholder="Add link label"
              value={link.name}
              onChange={(event) =>
                updateLink(index, "name", event.currentTarget.value)
              }
              required
            />
          </Grid.Col>
          <Grid.Col span={5}>
            <TextInput
              label="URL"
              placeholder="https://example.com"
              value={link.url}
              onChange={(event) =>
                updateLink(index, "url", event.currentTarget.value)
              }
              required
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <ActionIcon
              variant="transparent"
              color="red"
              onClick={() => deleteLink(index, link.id)} // Use optional chaining
              className="delete-icon"
            >
              <IconTrash size={32} />
            </ActionIcon>
          </Grid.Col>
        </Grid>
      );
    });

  // Render the modal content based on state
  const renderModalContent = () => {
    if (isLoadingQuickLinks) return <Loader />;

    if (isAddingLink || links.length > 0) {
      return (
        <div>
          {renderLinkForms()}
          {links.length < MAX_LINKS && (
            <Button
              variant="transparent"
              onClick={addLinkForm}
              mt="md"
              leftSection={<IconPlus size={24} />}
            >
              Add a link
            </Button>
          )}
          <div className="modal-footer">
            <Button variant="outline" onClick={resetForm} mt="md" radius={"md"}>
              Cancel
            </Button>
            <Button
              onClick={saveLinks}
              mt="md"
              radius={"md"}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <p className="no-links-message">
          You have no links added yet. Press the "Add a link" button.
        </p>
        <Button
          variant="transparent"
          onClick={addLinkForm}
          mt="md"
          leftSection={<IconPlus size={24} />}
        >
          Add a link
        </Button>
      </div>
    );
  };

  // Handle error safely
  if (isErrorQuickLinks && quickLinksError) {
    return <div>Error: {quickLinksError.message || "An error occurred"}</div>;
  }

  return (
    <div className="tax-pro-links">
      <Modal
        opened={opened}
        onClose={resetForm}
        title={
          <div className="modal-title">
            <div className="modal-title-icon">
              <IconBolt size={24} />
              <h3>Quick Links</h3>{" "}
            </div>{" "}
            <p className="modal-description">
              Add up to {MAX_LINKS} quick links.
            </p>
          </div>
        }
        size="xl"
      >
        {renderModalContent()}
      </Modal>

      <div className="tax-pro-links-header">
        <div className="links-header-title">
          <IconBolt />
          <h3>Quick Links</h3>
        </div>
        <div className="links-header-button">
          <Button
            variant="transparent"
            onClick={handleEditLinks}
            leftSection={<IconEdit size={16} />}
          >
            Edit
          </Button>
        </div>
      </div>

      <div className="links-content">
        {isLoadingQuickLinks ? <Loader /> : renderQuickLinks()}
      </div>
    </div>
  );
};

export default TaxProLinks;
